import * as React from 'react';
import { useState } from 'react';

import cn from 'classnames';

import { Button } from '@components/common';
import { CopySVG, PlusSVG, TrashSVG } from '@components/svgs';
import { ValidationErrorSVG } from '@components/svgs/ValidationErrorSVG';

import { DeleteRuleModal } from '../QuestionConfig/components';
import { OutcomeTagType, Rule, RuleLogic as RuleLogicType, RuleOutcome as RuleOutcomeType } from '../utils';

import { RuleLogic } from './RuleLogic';
import { RuleOutcome } from './RuleOutcome';
import { isRuleEmpty } from './useQuestionRules';

interface RuleContainerProps {
  rule: Rule;
  ruleIndex: number;
  question: ScreenerField;
  screenerFields: ScreenerField[];
  onUpdateRuleLogic: (operator: RuleLogicType['operator'], answer_value: RuleLogicType['answer_value']) => void;
  onUpdateRuleOutcome: (outcomeIndex: number, outcome: RuleOutcomeType) => void;
  onRemoveRule: (index: number) => void;
  onToggleOutcome: (ruleIndex: number) => void;
  onDuplicateRule: (ruleIndex: number) => void;
  groupedRules: Rule[];
  allowedOutcomeTypes?: OutcomeTagType[];
  isConflicting?: boolean;
}

// Constants
const MAX_OUTCOMES = 2;
const FIRST_OUTCOME_INDEX = 0;

// Helper functions
const generateRuleKey = (ruleIndex: number, suffix: string, data?: object): string => {
  const dataString = data ? `_${JSON.stringify(data)}` : '';
  return `${suffix}_${ruleIndex}${dataString}`;
};

const generateOutcomeKey = (ruleIndex: number, outcomeIndex: number, outcome: RuleOutcomeType): string =>
  `outcome_${ruleIndex}_${outcomeIndex}_${JSON.stringify(outcome)}`;

export const RuleContainer: React.FC<RuleContainerProps> = ({
  rule,
  ruleIndex,
  question,
  screenerFields,
  onUpdateRuleLogic,
  onUpdateRuleOutcome,
  onRemoveRule,
  onToggleOutcome,
  onDuplicateRule,
  groupedRules,
  allowedOutcomeTypes,
  isConflicting = false
}) => {
  const { logic: ruleLogic, outcomes: rule_outcomes } = rule;
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  const handleDuplicateRule = () => onDuplicateRule(ruleIndex);
  const handleRemoveRule = () => {
    onRemoveRule(ruleIndex);
    setShowDeleteModal(false);
  };
  const handleToggleOutcome = () => onToggleOutcome(ruleIndex);

  const showAndButton = rule_outcomes.length <= MAX_OUTCOMES;
  const isAndButtonActive = rule_outcomes.length === MAX_OUTCOMES;

  const isOnlyEmptyRule = groupedRules.length === 1 && isRuleEmpty(groupedRules[0]);

  return (
    <>
      <div
        key={generateRuleKey(ruleIndex, 'rule_container')}
        className={cn('rounded-md border bg-white p-4', isConflicting ? 'border-red-500' : 'border-gray-200')}
      >
        <header className='mb-4 flex items-center justify-between'>
          <span className='gray-800 text-sm font-bold'>Rule {ruleIndex + 1}</span>
          <div className='flex items-center space-x-2'>
            <button className='focus:outline-none' onClick={handleDuplicateRule} aria-label='Duplicate rule'>
              <CopySVG />
            </button>
            <button
              className='focus:outline-none'
              onClick={() => setShowDeleteModal(true)}
              aria-label='Delete rule'
              disabled={isOnlyEmptyRule}
            >
              <TrashSVG className={cn({ 'text-gray-300 opacity-90': isOnlyEmptyRule })} />
            </button>
          </div>
        </header>

        <div className='mb-4 rounded bg-gray-50 p-3'>
          <RuleLogic
            key={generateRuleKey(ruleIndex, 'rule_logic')}
            ruleId={generateRuleKey(ruleIndex, 'rule_logic')}
            ruleLogic={ruleLogic}
            question={question}
            onUpdateRuleLogic={onUpdateRuleLogic}
            allRules={groupedRules}
            currentRuleIndex={ruleIndex}
          />
        </div>

        <div className='space-y-4'>
          {rule_outcomes.map((ruleOutcome, outcomeIndex) => {
            const outcomeKey = generateOutcomeKey(ruleIndex, outcomeIndex, ruleOutcome);

            return (
              <React.Fragment key={outcomeKey}>
                <RuleOutcome
                  key={outcomeKey}
                  question={question}
                  fields={screenerFields}
                  ruleOutcome={ruleOutcome}
                  allRuleOutcomes={rule_outcomes}
                  onUpdateRuleOutcome={(outcome) => onUpdateRuleOutcome(outcomeIndex, outcome)}
                  allowedOutcomeTypes={allowedOutcomeTypes}
                />

                {outcomeIndex === FIRST_OUTCOME_INDEX && showAndButton && (
                  <Button
                    onClick={handleToggleOutcome}
                    className='gap-2'
                    active={isAndButtonActive}
                    aria-label={isAndButtonActive ? 'Remove second outcome' : 'Add second outcome'}
                  >
                    <PlusSVG />
                    <span>And</span>
                  </Button>
                )}
              </React.Fragment>
            );
          })}
        </div>

        {showDeleteModal && <DeleteRuleModal onClose={() => setShowDeleteModal(false)} onConfirm={handleRemoveRule} />}
      </div>

      {isConflicting && (
        <div className='mb-4 flex items-center gap-2 text-sm text-red-600'>
          <ValidationErrorSVG />
          <span>This conflicts with another rule.</span>
        </div>
      )}
    </>
  );
};
